import type { Locale } from '@application/Types/Locale'
import {
    AlternateUrlType,
    BlogContentType,
    HeroLandingViewType,
    HeroProfileComponentType,
    HeroSectionComponent,
    LocalSearchType,
    PageComponents,
    StrapiAgilitySectionItem,
    StrapiUspItem,
} from '@src/Application/Types/ComponentTypes'
import { Nullable, Opt } from '@src/Application/Types/Opt'
import { StrapiTranslations, Translations } from '@src/Application/Types/TranslationTypes'
import { convertPathToTitle, getPageUrl } from '@src/Application/Utils/generalUtils'
import {
    BLOG_DETAILS_INNER_COMPONENTS,
    MAP_ORGANIZATION_WITH_LANGUAGE,
    MAP_ORGANIZATION_WITH_SITEMAP_PAGES,
    STRAPI_COMPONENT_TYPES,
} from '@src/Core/Constants/PageConstants'
import { AllStrapiBlog } from '@src/Infrastructure/Gatsby/Node/createPages/blogPages/createBlogArticlePages'
import { AllStrapiFreightResources } from '@src/Infrastructure/Gatsby/Node/createPages/freightResourcesPages/createFreightResourcesPages'
import { AllStrapiGlobalFreight } from '@src/Infrastructure/Gatsby/Node/createPages/globalFreightPages/createGlobalFreightPages'
import { AllStrapiHelpcenter } from '@src/Infrastructure/Gatsby/Node/createPages/helpCenterPages/createHelpCenterArticlePages'
import { AllStrapiBlogCategory } from '@src/Infrastructure/Gatsby/Node/createPages/sitemap/createSitemapPage'
import { AllStrapiPage } from '@src/Infrastructure/Gatsby/Node/createPages/strapiPages/createStrapiPages'
import { Organization } from '@src/Infrastructure/Types/OrganizationType'
import { localizeUrl } from '@src/Infrastructure/Url/localizeUrls/localizeUtilities'

const DISPLAY_BREADCRUMB_AFTER = [
    STRAPI_COMPONENT_TYPES.HERO_SECTION,
    STRAPI_COMPONENT_TYPES.HERO_PROFILE,
    STRAPI_COMPONENT_TYPES.HERO_LANDING_VIEW,
    STRAPI_COMPONENT_TYPES.USP_SECTION,
    // STRAPI_COMPONENT_TYPES.AGILITY_SECTION,
]

export const findBreadcrumbIndex = (pageComponents: PageComponents[]): number => {
    let index = 0

    for (const pageComponent of pageComponents) {
        if (DISPLAY_BREADCRUMB_AFTER.includes(pageComponent.strapi_component)) {
            index++
        } else {
            break
        }
    }

    return index
}

type LinkType = { link: string; label: string }

export const sortLinksByLabel = (a: LinkType, b: LinkType): number => a.label.localeCompare(b.label)

export const getSitemapAlternativeUrls = (locale: Locale): AlternateUrlType[] => {
    const organization = process.env.GATSBY_ORGANIZATION as Organization
    const sitemapPages = MAP_ORGANIZATION_WITH_SITEMAP_PAGES[organization]
    const alternatePages = sitemapPages.filter((page) => page.locale !== locale)

    return alternatePages.map((page) => ({
        pageUrl: page.pageUrl,
        language: page.locale,
        legacyPageUrl: null,
        useLegacyPageUrl: false,
    }))
}

export const getCategoryAlternateUrls = (
    categoryAlternateUrls: AlternateUrlType[],
    alternateUrls: Nullable<AlternateUrlType[]>
): Nullable<AlternateUrlType[]> => {
    return categoryAlternateUrls.length ? categoryAlternateUrls : alternateUrls
}

export const isValidPageLink = (node: { pageUrl: string; language: Locale }, locale: Locale): boolean =>
    !node.pageUrl.includes('do-not-remove') && node.language === locale

export const isNoIndexNoFollowLink = ({ metaFollow, metaIndex }: { metaIndex: string; metaFollow: string }): boolean =>
    metaIndex === 'noindex' || metaFollow === 'nofollow'

export type ArticleNodeType = {
    locale: Locale
    pageUrl: string
    legacyPageUrl: Nullable<string>
    useLegacyPageUrl: boolean
    createdAt: string
    updatedAt: string
    metaIndex: string
    metaFollow: string
    id: string
    title: {
        data: { title: string }
    }
}

export const getBlogAndHelpCenterArticleLinks = (
    nodes: AllStrapiHelpcenter['nodes'] | AllStrapiBlog['nodes'],
    locale: Locale
): LinkType[] => {
    return nodes
        .filter((node) => isValidPageLink(node, locale))
        .filter((node) => !isNoIndexNoFollowLink({ metaFollow: node.metaFollow, metaIndex: node.metaIndex }))
        .map((node) => ({
            link: localizeUrl(node.language, getPageUrl(node)),
            label: node.title.data.title,
        }))
        .sort(sortLinksByLabel)
}

export const getGlobalFreightAndResourcesLinks = (
    edges: AllStrapiGlobalFreight['edges'] | AllStrapiFreightResources['edges'],
    locale: Locale
): LinkType[] => {
    return edges
        .filter(({ node }) => isValidPageLink(node, locale))
        .filter(({ node }) => !isNoIndexNoFollowLink({ metaFollow: node.metaFollow, metaIndex: node.metaIndex }))
        .map(({ node }) => ({
            link: localizeUrl(node.language, getPageUrl(node)),
            label: node.title,
        }))
        .sort(sortLinksByLabel)
}

export const getAllPageLinks = (edges: AllStrapiPage['edges'], locale: Locale, homeTranslation: string): LinkType[] => {
    return edges
        .filter(({ node }) => isValidPageLink(node, locale))
        .filter(({ node }) => !isNoIndexNoFollowLink({ metaFollow: node.metaFollow, metaIndex: node.metaIndex }))
        .map(({ node }) => ({
            link: localizeUrl(node.language, getPageUrl(node)),
            label: node.pageUrl === '/' ? homeTranslation : convertPathToTitle(node.pageUrl),
        }))
        .sort(sortLinksByLabel)
}

export const getCategoriesLinks = (nodes: AllStrapiBlogCategory['nodes'], locale: Locale): LinkType[] => {
    return nodes
        .filter((node) => isValidPageLink({ pageUrl: node.categoryUrl, language: node.language }, locale))
        .map((node) => ({
            link: localizeUrl(node.language, node.categoryUrl),
            label: node.title,
        }))
        .sort(sortLinksByLabel)
}

export const mapHeroSectionToHeroProfileComponent = (
    heroSection: HeroSectionComponent,
    localSearch: Opt<LocalSearchType>
): HeroProfileComponentType => {
    return {
        id: heroSection.id,
        strapi_component: STRAPI_COMPONENT_TYPES.HERO_PROFILE,
        title: '',
        header: heroSection.header,
        description: heroSection.description,
        backgroundImage: heroSection.backgroundImage,
        imageLocation: 'Background',
        mode: heroSection.mode,
        showProfileSection: heroSection.showQuoteForm ?? false,
        showHelpCenterSearchForm: false,
        showUSPs: false,
        showAgilitySection: false,
        localSearch,
        MiddleContent: heroSection.MiddleContent,
        showLogoCarousel: false,
    }
}

export const mapHeroLandingToHeroProfileComponent = (
    heroLanding: HeroLandingViewType,
    localSearch: Opt<LocalSearchType>
): HeroProfileComponentType => {
    return {
        id: heroLanding.id,
        strapi_component: STRAPI_COMPONENT_TYPES.HERO_PROFILE,
        title: heroLanding.landingTitle,
        header: {
            data: {
                header: heroLanding.description?.data.description ?? '',
                childMarkdownRemark: {
                    html: heroLanding.description?.data.childMarkdownRemark.html ?? '',
                },
            },
        },
        description: heroLanding.subDescription
            ? {
                  data: {
                      description: heroLanding.subDescription.data.subDescription,
                      childMarkdownRemark: {
                          html: heroLanding.subDescription.data.childMarkdownRemark.html,
                      },
                  },
              }
            : null,

        backgroundImage: heroLanding.image,
        imageLocation: 'Below Content',
        mode: heroLanding.mode,
        showProfileSection: heroLanding.showQuoteForm ?? false,
        showHelpCenterSearchForm: heroLanding.showHelpCenterSearchForm,
        showUSPs: false,
        showAgilitySection: false,
        localSearch,
        showLogoCarousel: false,
    }
}

export const mapUspAndAgilityToHeroProfile = ({
    heroProfile,
    singleTypeUSP,
    singleTypeAgilitySection,
    helpCenterLocalSearch,
}: {
    heroProfile: HeroProfileComponentType
    singleTypeUSP: Opt<StrapiUspItem[]>
    singleTypeAgilitySection: Opt<StrapiAgilitySectionItem>
    helpCenterLocalSearch: Opt<LocalSearchType>
}): HeroProfileComponentType => {
    return {
        ...heroProfile,
        ...(helpCenterLocalSearch ? { localSearch: helpCenterLocalSearch } : {}),
        ...(singleTypeUSP
            ? {
                  usp: {
                      id: `usp-${heroProfile.id}`,
                      strapi_component: STRAPI_COMPONENT_TYPES.USP_SECTION,
                      items: singleTypeUSP,
                  },
              }
            : {}),
        ...(singleTypeAgilitySection
            ? {
                  agilitySection: {
                      id: `agility-section-${heroProfile.id}`,
                      strapi_component: STRAPI_COMPONENT_TYPES.AGILITY_SECTION,
                      logo: singleTypeAgilitySection.logo,
                      logoPrefix: singleTypeAgilitySection.logoPrefix,
                      agilityDescription: singleTypeAgilitySection.description,
                  },
              }
            : {}),
    }
}

export const filterUspAndAgilitySection = ({
    pageComponents,
    removeUsp,
    removeAgility,
}: {
    pageComponents: PageComponents[]
    removeUsp: boolean
    removeAgility: boolean
}): PageComponents[] => {
    return pageComponents.filter((component) => {
        return (
            (component.strapi_component !== STRAPI_COMPONENT_TYPES.USP_SECTION || !removeUsp) &&
            (component.strapi_component !== STRAPI_COMPONENT_TYPES.AGILITY_SECTION || !removeAgility)
        )
    })
}

export const getLocalSearchLocalWise = ({
    localSearchHelpCenter,
    locale,
}: {
    localSearchHelpCenter: LocalSearchType
    locale: Locale
}): LocalSearchType => {
    const { index, store } = localSearchHelpCenter
    const localeFilteredStore = Object.values(store).reduce((prev, curr) => {
        if (curr.locale === locale) {
            prev[curr.id] = curr
        }

        return prev
    }, {} as LocalSearchType['store'])

    return { index, store: localeFilteredStore }
}

export const modifiedHeroComponents = ({
    pageComponents,
    singleTypeUSP,
    singleTypeAgilitySection,
    helpCenterLocalSearch,
}: {
    pageComponents: PageComponents[]
    singleTypeUSP: Opt<StrapiUspItem[]>
    singleTypeAgilitySection: Opt<StrapiAgilitySectionItem>
    helpCenterLocalSearch?: Opt<LocalSearchType>
}): PageComponents[] => {
    let showUSP = false
    let showAgility = false

    const components = pageComponents.map((component) => {
        if (component.strapi_component === STRAPI_COMPONENT_TYPES.HERO_SECTION) {
            return mapHeroSectionToHeroProfileComponent(component as HeroSectionComponent, helpCenterLocalSearch)
        }
        if (component.strapi_component === STRAPI_COMPONENT_TYPES.HERO_LANDING_VIEW) {
            return mapHeroLandingToHeroProfileComponent(component as HeroLandingViewType, helpCenterLocalSearch)
        }
        if (component.strapi_component === STRAPI_COMPONENT_TYPES.HERO_PROFILE) {
            const heroProfile = component as HeroProfileComponentType
            showUSP = !!heroProfile.showUSPs
            showAgility = !!heroProfile.showAgilitySection
            return mapUspAndAgilityToHeroProfile({
                heroProfile,
                singleTypeUSP,
                singleTypeAgilitySection,
                helpCenterLocalSearch,
            })
        }
        return component
    })

    /*
        remove usp and agility components from page component,
        if hero profile has enable showUSP, singleTypeAgilitySection
    */
    if (showUSP || showAgility) {
        return filterUspAndAgilitySection({
            pageComponents: components,
            removeUsp: showUSP,
            removeAgility: showAgility,
        })
    }

    return components
}

export const getReadingTime = (pageComponents: PageComponents[]): number => {
    return pageComponents.reduce((prev, component) => {
        if (component.strapi_component === STRAPI_COMPONENT_TYPES.BLOG_CONTENT) {
            const cast = component as unknown as BlogContentType

            return prev + cast.content.data.childMarkdownRemark.fields.readingTime.minutes
        }

        return prev
    }, 0)
}

export const getInnerComponentsForBlogDetails = (pageComponents: PageComponents[]): PageComponents[] => {
    return pageComponents.filter((component) => BLOG_DETAILS_INNER_COMPONENTS.includes(component.strapi_component))
}

export const getFirstIndexInnerComponent = (pageComponents: PageComponents[]): number => {
    const index = pageComponents.findIndex((component) =>
        BLOG_DETAILS_INNER_COMPONENTS.includes(component.strapi_component)
    )
    return index < 0 ? pageComponents.length : index
}

export const getTranslations = (strapiTranslations: StrapiTranslations): Translations => {
    const organization = process.env.GATSBY_ORGANIZATION as Organization
    const availableLanguages = MAP_ORGANIZATION_WITH_LANGUAGE[organization]

    return availableLanguages.reduce((acc, lang, index) => {
        acc[lang as Locale] =
            lang === 'en' ? strapiTranslations : strapiTranslations?.localizations?.data?.[index - 1]?.attributes

        return acc
    }, {} as Translations)
}
